import { useEffect, useState, useMemo } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Plus, Search } from 'lucide-react'
import { getEstimates } from '../services/api'
import { formatCurrency, formatDate } from '../lib/formatters'
import { useSort } from '../hooks/useSort'
import SortHeader from '../components/ui/SortHeader'
import Pagination from '../components/ui/Pagination'
import StatusBadge from '../components/ui/StatusBadge'
import Avatar from '../components/ui/Avatar'
import EmptyState from '../components/ui/EmptyState'

const STATUSES = ['ALL', 'DRAFT', 'APPROVED', 'SENT', 'ACCEPTED', 'DECLINED', 'EXPIRED']

export default function Estimates() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [allEstimates, setAllEstimates] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState(searchParams.get('search') || '')
  const status = searchParams.get('status') || 'ALL'

  useEffect(() => {
    setLoading(true)
    getEstimates({ take: 9999 })
      .then((res) => setAllEstimates(res.data.estimates || []))
      .catch(() => setAllEstimates([]))
      .finally(() => setLoading(false))
  }, [])

  const statusCounts = useMemo(() => {
    const counts = { ALL: allEstimates.length }
    STATUSES.forEach((s) => { if (s !== 'ALL') counts[s] = 0 })
    allEstimates.forEach((est) => {
      if (counts[est.status] !== undefined) counts[est.status]++
    })
    return counts
  }, [allEstimates])

  const filtered = useMemo(() => {
    let list = allEstimates
    if (status !== 'ALL') list = list.filter((est) => est.status === status)
    if (search.trim()) {
      const q = search.trim().toLowerCase()
      list = list.filter((est) =>
        (est.number || '').toLowerCase().includes(q) ||
        (est.customer?.name || '').toLowerCase().includes(q)
      )
    }
    return list
  }, [allEstimates, status, search])

  const { paged, page, setPage, totalPages, total, sortKey, sortDir, toggleSort, pageSize } = useSort(filtered, {
    defaultSort: 'date', defaultDir: 'desc', pageSize: 25, storageKey: 'estimates',
  })

  const setStatus = (s) => {
    const p = new URLSearchParams(searchParams)
    if (s === 'ALL') p.delete('status'); else p.set('status', s)
    setSearchParams(p)
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-gray-900">Estimates</h1>
        <Link to="/estimates/new" className="btn-primary flex items-center gap-1.5">
          <Plus className="h-4 w-4" /> New Estimate
        </Link>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input type="text" placeholder="Search estimates..." className="input pl-9" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div className="flex gap-1 flex-wrap">
          {STATUSES.map((s) => (
            <button key={s} onClick={() => setStatus(s)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${status === s ? 'bg-brand-100 text-brand-700' : 'text-gray-500 hover:bg-gray-100'}`}>
              {s}<span className={`ml-1 ${status === s ? 'text-brand-500' : 'text-gray-400'}`}>{statusCounts[s] ?? 0}</span>
            </button>
          ))}
        </div>
      </div>
      <div className="card overflow-hidden">
        {loading ? (
          <div className="space-y-3 p-4">{[...Array(5)].map((_, i) => (<div key={i} className="h-10 bg-gray-100 rounded animate-pulse" />))}</div>
        ) : filtered.length === 0 ? (
          <EmptyState type="estimates" message={status !== 'ALL' || search ? 'No estimates match your filters.' : 'No estimates yet. Create your first one.'} actionLabel={status === 'ALL' && !search ? 'New Estimate' : undefined} actionTo={status === 'ALL' && !search ? '/estimates/new' : undefined} />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 bg-gray-50 border-b">
                    <SortHeader label="Estimate #" sortKey="number" currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} />
                    <SortHeader label="Customer" sortKey="customer.name" currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} />
                    <SortHeader label="Date" sortKey="date" currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} />
                    <SortHeader label="Amount" sortKey="total" currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} align="right" />
                    <SortHeader label="Status" sortKey="status" currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} />
                  </tr>
                </thead>
                <tbody>
                  {paged.map((est) => (
                    <tr key={est.id} className="border-b last:border-0 hover:bg-gray-50/70 transition-colors">
                      <td className="px-4 py-3"><Link to={`/estimates/${est.id}`} className="text-brand-600 hover:underline font-medium">{est.number || 'DRAFT'}</Link></td>
                      <td className="px-4 py-3"><div className="flex items-center gap-2"><Avatar name={est.customer?.name} /><span className="text-gray-600">{est.customer?.name || '—'}</span></div></td>
                      <td className="px-4 py-3 text-gray-500">{formatDate(est.date)}</td>
                      <td className="px-4 py-3 text-right">{formatCurrency(est.total, est.currency)}</td>
                      <td className="px-4 py-3"><StatusBadge status={est.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination page={page} totalPages={totalPages} total={total} pageSize={pageSize} onPageChange={setPage} />
          </>
        )}
      </div>
    </div>
  )
}
